import React, { FC, ReactNode } from 'react';

import { useDispatch, useSelector } from 'react-redux';

// Shared UI Library Components
import { CustomIcon, CustomTag, EIconName } from '@ppmcore/seven-ppm-core-shared-components-react';


// Styles
import './filters-list.scss';

// Store entities
import { saveCounsellorsFilters } from '../../../../store/counsellors/counsellors.thunks';
import { getCoreState } from '../../../../store/core/core.selectors';
import { getCounsellorsState } from '../../../../store/counsellors/counsellors.selectors';

// Models
import { ICounsellorsFilters } from '../../../../interfaces/counsellors.interfaces';
import { getCurrencyState } from '../../../../store/currency/currency.selectors';

type TFiltersListProps = {};

export const FiltersList: FC<TFiltersListProps> = (
  {}: TFiltersListProps
) => {

  const { filters } = useSelector(getCounsellorsState);

  const {
    languages: languagesList,
    countries: countriesList,
    speciality,
    specializations,
  } = useSelector(getCoreState);
  const { currentCurrency } = useSelector(getCurrencyState)

  const dispatch = useDispatch<any>();

  const generateInfoObject = (list: { name: string, id?: number, code?: string }[], key: 'id' | 'code' = 'code'): {
    [key: string]: string
  } => {
    return list.reduce((acc, item) => ({ ...acc, [item[key] as string]: item.name }), {});
  }

  const hasFilters = (filters: ICounsellorsFilters) => {
    return Object.keys(filters)
      .filter(key => !['offset', 'limit', 'search'].includes(key))
      .map((key) => filters[key as keyof ICounsellorsFilters])
      .some((value) => Array.isArray(value) ? value?.length > 0 : !!value);
  };

  const languagesObject = generateInfoObject(languagesList?.list);
  const countriesObject = generateInfoObject(countriesList?.list);
  const specialityObject = generateInfoObject(speciality?.list, 'id');
  const specializationsObject = generateInfoObject(specializations?.list, 'id');

  const statusObject: { [key: string]: string } = {
    active: 'Active',
    in_active: 'Inactive',
    archived: 'Archived',
  };

  const hiddenList = !hasFilters(filters);

  const onRemoveFilterByParam = (
    key: keyof ICounsellorsFilters,
    val?: string | number
  ): void => {
    dispatch(saveCounsellorsFilters({
      ...filters,
      [key]: Array.isArray(filters[key]) ? (filters[key] as Array<string | number>)
        .filter((pValue) => pValue !== val) : undefined,
      ...(key === 'specialities' && { specialization: [] }),
    }));
  };

  const onClearAll = (): void => {
    dispatch(saveCounsellorsFilters({ offset: filters.offset, limit: filters.limit }));
  }

  const renderTags = (
    filterArray: string[] | number[] | undefined | null, onClose: (value: string | number) => void,
    displayFormatter?: (value: string | number) => string | ReactNode
  ): string | ReactNode => {
    if (!filterArray?.length) return '';
    return filterArray?.map((value) => (
      <CustomTag key={ value } closeIcon={ true } onClose={ () => onClose(value) }>
        { displayFormatter ? displayFormatter(value) : value }
      </CustomTag>
    ));
  };

  return (
    <div className={ `filters-list` } hidden={ hiddenList }>
      <div className="filters-list--items">

        {
          renderTags(
            filters?.specialities,
            (value) => onRemoveFilterByParam('specialities', value),
            (value) => specialityObject[value]
          )
        }

        {
          renderTags(
            filters?.specialization,
            (value) => onRemoveFilterByParam('specialization', value),
            (value) => specializationsObject[value]
          )
        }

        {
          renderTags(
            filters?.languages, (value) => onRemoveFilterByParam('languages', value),
            (value) => languagesObject[value]
          )
        }

        {
          renderTags(
            filters?.countries,
            (value) => onRemoveFilterByParam('countries', value),
            (value) => countriesObject[value]
          )
        }

        { !!filters?.rate_from && (
          <CustomTag closeIcon={ true } onClose={ () => onRemoveFilterByParam('rate_from') }>
            Min: { filters.rate_from }{ currentCurrency?.symbol ?? '' }
          </CustomTag>
        ) }

        { !!filters?.rate_to && (
          <CustomTag closeIcon={ true } onClose={ () => onRemoveFilterByParam('rate_to') }>
            Max: { filters.rate_to }{ currentCurrency?.symbol ?? '' }
          </CustomTag>
        ) }

        {
          renderTags(
            filters?.review_rating,
            (value) => onRemoveFilterByParam('review_rating', value),
            renderRatingStars
          )
        }

        {
          renderTags(
            filters?.status,
            (value) => onRemoveFilterByParam('status', value),
            (value) => statusObject[value]
          )
        }

      </div>
      <div className="filters-list--clear-action" onClick={ onClearAll }>
        Clear All <CustomIcon name={ EIconName.Close } size={ 'size-md' }/>
      </div>
    </div>
  );
}

const renderRatingStars = (rating: string | number): ReactNode => (
  <span className="rating-item">
    { Array(Number(rating))
      .fill(null)
      .map((_, idx) => (
        <CustomIcon key={ idx } size={ 'size-md' } name={ EIconName.StarFill }/>
      )) }
  </span>
);
